import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sheet, Link2, Unlink, CheckCircle2, Lock, Globe, LogOut } from "lucide-react";
import { t, isEn } from "../i18n";

export function SheetsModal({ open, onOpenChange, overlayId, config, setConfig }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [google, setGoogle] = useState({ sheets: false, email: null });
  const [googleBusy, setGoogleBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUrl(config?.url || "");
    api.get("/auth/google/status").then(({ data }) => setGoogle(data)).catch(() => {});
  }, [open, config]);

  const connect = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/overlays/${overlayId}/sheets/connect`, { url: url.trim() });
      setConfig(data.sheets);
      toast.success(data.sheets?.source === "private" ? t("Planilha privada conectada") : t("Planilha conectada"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Falha ao conectar planilha"));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete(`/overlays/${overlayId}/sheets`);
      setConfig({ connected: false });
      setUrl("");
      toast.success(t("Planilha desconectada"));
    } catch { toast.error(t("Falha ao desconectar")); }
  };

  const connectGoogle = async () => {
    setGoogleBusy(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const { data } = await api.get("/auth/google/start-sheets", { params: { overlay_id: overlayId, origin: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Falha ao iniciar a vinculação do Google Sheets"));
      setGoogleBusy(false);
    }
  };

  const disconnectGoogle = async () => {
    try {
      await api.delete("/auth/google/sheets");
      setGoogle((g) => ({ ...g, sheets: false }));
      toast.success(t("Acesso ao Google Sheets desvinculado"));
    } catch { toast.error(t("Falha ao desvincular o Google Sheets")); }
  };

  const connected = !!config?.connected;
  const isPrivate = config?.source === "private";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sheets-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Sheet className="h-5 w-5 text-primary" /> Google Sheets</DialogTitle>
          <DialogDescription>
            {t("Cole o link de uma planilha pública, ou conecte sua conta Google para usar planilhas privadas. Depois vincule textos e imagens a células nas Propriedades de cada elemento.")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 bg-black/30 border border-border rounded-sm px-3 py-2" data-testid="google-account-row">
          {google.sheets ? (
            <>
              <span className="text-xs text-emerald-400 flex items-center gap-1.5 min-w-0">
                <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{t("Google Sheets vinculado")}{google.email ? `: ${google.email}` : ""}</span>
              </span>
              <button onClick={disconnectGoogle} className="text-[11px] text-muted-foreground hover:text-destructive flex items-center gap-1 flex-shrink-0" data-testid="google-disconnect-btn">
                <LogOut className="h-3 w-3" /> {t("desvincular")}
              </button>
            </>
          ) : (
            <>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" /> {t("Planilhas privadas")}
              </span>
              <Button onClick={connectGoogle} disabled={googleBusy} size="sm" variant="secondary" className="rounded-sm h-7 text-xs" data-testid="google-connect-btn">
                {googleBusy ? t("Redirecionando…") : t("Vincular Google Sheets")}
              </Button>
            </>
          )}
        </div>

        {connected && (
          <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-3 py-2" data-testid="sheets-connected">
            <CheckCircle2 className="h-4 w-4" /> {t("Conectada")} · ID {config.sheet_id?.slice(0, 10)}…
            <span className={`ml-auto flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm ${isPrivate ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15"}`} data-testid="sheets-source-badge">
              {isPrivate ? <><Lock className="h-3 w-3" /> {t("Privada")}</> : <><Globe className="h-3 w-3" /> {t("Pública")}</>}
            </span>
          </div>
        )}

        <Input
          placeholder="https://docs.google.com/spreadsheets/d/..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          data-testid="sheets-url-input"
        />
        <div className="flex gap-2">
          <Button onClick={connect} disabled={busy} className="flex-1 rounded-sm" data-testid="sheets-connect-btn">
            <Link2 className="h-4 w-4 mr-2" /> {busy ? t("Conectando…") : connected ? t("Reconectar") : t("Conectar")}
          </Button>
          {connected && (
            <Button onClick={disconnect} variant="outline" className="rounded-sm" data-testid="sheets-disconnect-btn">
              <Unlink className="h-4 w-4 mr-2" /> {t("Desconectar")}
            </Button>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground">{isEn() ? <>Tip: use a range like <code>A2:A20</code> for lists, or <code>Sheet1!B2</code> for a specific cell.</> : <>Dica: use um intervalo como <code>A2:A20</code> para listas, ou <code>Página1!B2</code> para uma célula específica.</>}</p>
        <p className="text-[10px] text-muted-foreground">{isEn() ? <>Images: paste the image's <b>direct URL</b> into the cell, or use the formula <code>=IMAGE("https://…")</code> (<code>=IMAGE</code> is only read from a private sheet connected via Google). <b className="text-amber-400">Note:</b> "Insert &gt; Image &gt; Insert image in cell" <b>doesn't work</b> — Google doesn't expose that image through the API.</> : <>Imagens: cole a <b>URL direta</b> da imagem na célula, ou use a fórmula <code>=IMAGE("https://…")</code> (o <code>=IMAGE</code> só é lido em planilha privada conectada via Google). <b className="text-amber-400">Atenção:</b> "Inserir &gt; Imagem &gt; Inserir imagem na célula" <b>não funciona</b> — o Google não expõe essa imagem pela API.</>}</p>
      </DialogContent>
    </Dialog>
  );
}
